'use client';
import Image from "next/image";
import React from "react";
import { motion } from "framer-motion";

interface CareerApplicationPortalProps {
  title: string;
  subtitle: string;
  submitCvText: string;
  submitCvLink: string;
}

export default function CareerApplicationPortal({
  title,
  subtitle,
  submitCvText,
  submitCvLink,
}: CareerApplicationPortalProps) {
  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div 
      className="mx-auto"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      <div className="relative bg-white overflow-hidden">
        {/* Background Pattern */}
        <Image
          src="/images/career/career-submit-cv-bg.svg"
          alt="Background pattern"
          fill
          className="object-cover object-bottom pointer-events-none select-none"
        />
        {/* Content */}
        <div className="relative z-10 py-16 lg:py-20 px-6 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-6 mb-8">
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-medium text-black leading-tight capitalize">
                {title}
              </h1>
              <p className="text-lg lg:text-xl text-gray-800 font-medium leading-relaxed max-w-3xl mx-auto">
                {subtitle}
              </p>
            </div>

            {/* Submit CV Button */}
            <button
              className="inline-flex items-center gap-3 bg-primary-800 hover:bg-primary-800/90 text-white font-medium px-6 py-4 rounded-full transition-colors duration-200 group cursor-pointer"
              onClick={() => window.open(submitCvLink, "_blank")}
            >
              <svg
                className="w-2 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.91598 7.37487L2.03865 12.2106C1.83065 12.4187 1.49731 12.4187 1.33065 12.2106L1.03865 11.9185C0.989752 11.8735 0.950722 11.8188 0.924023 11.758C0.897324 11.6971 0.883539 11.6314 0.883539 11.565C0.883539 11.4985 0.897324 11.4328 0.924023 11.3719C0.950722 11.3111 0.989752 11.2564 1.03865 11.2114L5.29198 7.00001L1.03865 2.83126C0.989537 2.78627 0.950323 2.73155 0.923495 2.67057C0.896667 2.6096 0.882812 2.5437 0.882812 2.47708C0.882812 2.41046 0.896667 2.34457 0.923495 2.2836C0.950323 2.22262 0.989537 2.1679 1.03865 2.12291L1.33198 1.83076C1.49865 1.62266 1.83198 1.62266 2.03998 1.83076L6.95865 6.66651C7.04277 6.76793 7.08517 6.89757 7.07725 7.02913C7.06933 7.16068 7.01167 7.28429 6.91598 7.37487Z"
                  fill="currentColor"
                />
              </svg>
              <span className="capitalize">{submitCvText}</span>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
